import createFetchClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './generated/schema.js';

export type { paths, components } from './generated/schema.js';

/**
 * The OpenRoad API client.
 *
 * Types come from `src/generated/schema.ts`, which is generated from
 * `apps/server/openapi/openapi.json` — itself emitted from the Zod schemas that
 * validate requests at runtime. So a route's shape, its runtime validation, its
 * documentation and this client's types are all the same declaration. Changing
 * one without the others is not possible; it shows up as a diff in the
 * committed contract.
 *
 * Regenerate with `pnpm --filter @openroad/api-client generate`.
 */

export interface TokenStore {
  getAccessToken(): string | null | Promise<string | null>;
  getRefreshToken(): string | null | Promise<string | null>;
  /** Called after a successful refresh, and with nulls when the session ends. */
  setTokens(tokens: { accessToken: string; refreshToken: string } | null): void | Promise<void>;
}

/** Keeps tokens in memory only. The default, and correct for a fresh page load. */
export class MemoryTokenStore implements TokenStore {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  getAccessToken() {
    return this.accessToken;
  }
  getRefreshToken() {
    return this.refreshToken;
  }
  setTokens(tokens: { accessToken: string; refreshToken: string } | null) {
    this.accessToken = tokens?.accessToken ?? null;
    this.refreshToken = tokens?.refreshToken ?? null;
  }
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

/** Thrown by {@link unwrap} when a call fails. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface CreateClientOptions {
  /** Where the API lives — "http://localhost:3001/api". */
  baseUrl: string;
  tokens?: TokenStore;
  fetch?: typeof globalThis.fetch;
}

export function createClient(options: CreateClientOptions) {
  const tokens = options.tokens ?? new MemoryTokenStore();

  const client = createFetchClient<paths>({
    baseUrl: options.baseUrl,
    fetch: options.fetch,
  });

  /**
   * Attaches the access token, and on a 401 tries exactly one refresh before
   * giving up.
   *
   * `refreshing` collapses concurrent 401s onto a single refresh. Without it,
   * a screen that fires five requests at once when the token has just expired
   * would send five refreshes — and since refresh tokens rotate, four of them
   * would present an already-rotated token, which the server correctly treats
   * as theft and answers by ending every session the member has.
   */
  let refreshing: Promise<boolean> | null = null;

  async function refreshSession(): Promise<boolean> {
    const refreshToken = await tokens.getRefreshToken();
    if (!refreshToken) return false;

    const response = await (options.fetch ?? globalThis.fetch)(
      `${options.baseUrl.replace(/\/$/, '')}/auth/refresh`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      },
    );

    if (!response.ok) {
      await tokens.setTokens(null);
      return false;
    }

    const body = (await response.json()) as { accessToken: string; refreshToken: string };
    await tokens.setTokens({ accessToken: body.accessToken, refreshToken: body.refreshToken });
    return true;
  }

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const accessToken = await tokens.getAccessToken();
      if (accessToken) request.headers.set('authorization', `Bearer ${accessToken}`);
      return request;
    },
    async onResponse({ request, response }) {
      if (response.status !== 401) return response;
      // A failing refresh must not recurse into itself.
      if (request.url.endsWith('/auth/refresh')) return response;

      refreshing ??= refreshSession().finally(() => {
        refreshing = null;
      });

      if (!(await refreshing)) return response;

      const accessToken = await tokens.getAccessToken();
      const retry = new Request(request.url, request);
      if (accessToken) retry.headers.set('authorization', `Bearer ${accessToken}`);

      return (options.fetch ?? globalThis.fetch)(retry);
    },
  };

  client.use(authMiddleware);

  /**
   * A plain `fetch` carrying the session, for the routes openapi-fetch cannot
   * model.
   *
   * Smolit answers as a server-sent event stream: the body is consumed a chunk
   * at a time, which is not something a typed JSON client has a shape for. The
   * temptation is to write a one-line `fetch` with the token in the caller —
   * and that is exactly the mistake ADR-0008 warns about, because it would
   * refresh independently of everything else on the page. Several requests
   * meeting a just-expired token would each rotate it, and all but the first
   * would present an already-rotated token, which the server reads as theft and
   * answers by ending every session the member has.
   *
   * So this shares `refreshing` with the typed client. One refresh, whoever
   * needed it.
   */
  async function authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const url = `${options.baseUrl.replace(/\/$/, '')}${path}`;
    const doFetch = options.fetch ?? globalThis.fetch;

    const send = async () => {
      const headers = new Headers(init.headers);
      const accessToken = await tokens.getAccessToken();
      if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
      return doFetch(url, { ...init, headers });
    };

    const response = await send();
    if (response.status !== 401) return response;

    refreshing ??= refreshSession().finally(() => {
      refreshing = null;
    });
    if (!(await refreshing)) return response;

    return send();
  }

  return { ...client, tokens, authorizedFetch };
}

/**
 * Turn an openapi-fetch result into a value or a throw.
 *
 * openapi-fetch returns `{ data, error }` so both paths are typed. That is the
 * right default, but most call sites want the value, and a `try`/`catch` reads
 * better there than an `if (error)` on every line.
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await call;

  if (error !== undefined || !response.ok) {
    const body = error as ApiErrorBody | undefined;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? fallbackMessage(response.status),
      body?.error?.details,
    );
  }

  return data as T;
}

/**
 * What to say when the failure carried no message of its own.
 *
 * The API always sends `{ error: { code, message } }`, so reaching here means
 * the response did not come from the API: a proxy that has no upstream, a
 * gateway timeout, a network that dropped. Those land in front of a member as
 * often as any handled error — a single-replica deployment answers 502 or 503
 * for the few seconds it takes to restart — and "Request failed with status
 * 503" is not a sentence to show somebody who was saving their car.
 *
 * Anything unrecognised keeps the status in the text, because a message that
 * says nothing at all is worse to debug and no kinder to read.
 */
function fallbackMessage(status: number): string {
  if (status === 502 || status === 503 || status === 504) {
    return 'The server is not answering just now — it may be restarting. Please try again in a moment.';
  }
  if (status === 429) {
    return 'That is a lot of requests in a short time. Please wait a moment and try again.';
  }
  if (status >= 500) {
    return 'Something went wrong on our side. Please try again in a moment.';
  }
  return `Request failed with status ${status}`;
}

export type OpenRoadClient = ReturnType<typeof createClient>;
